import React, { useEffect, useState } from 'react';
import FormContainer from './FormContainer';
import Button from 'react-bootstrap/Button';
import './UIVisualizador.css';

interface UIVisualizadorProps {
    show: boolean;
    onClose: () => void;
    tipo: 'foto' | 'documento';
    items?: Array<{ source: string; tipo?: string }>;
    currentIndex?: number;
    documentoUrl?: string;
}

export default function UIVisualizador({
    show,
    onClose,
    tipo,
    items = [],
    currentIndex = 0,
    documentoUrl
}: UIVisualizadorProps) {
    const [index, setIndex] = useState(currentIndex);

    useEffect(() => {
        setIndex(currentIndex);
    }, [currentIndex]);

    // Navegação por teclado
    useEffect(() => {
        if (!show || tipo !== 'foto') return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                handlePrevious();
            } else if (e.key === 'ArrowRight') {
                handleNext();
            } else if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [show, tipo, index, items.length]);

    const handleNext = () => {
        if (index < items.length - 1) {
            setIndex(index + 1);
        }
    };

    const handlePrevious = () => {
        if (index > 0) {
            setIndex(index - 1);
        }
    };

    const currentItem = items[index];
    const imageUrl = currentItem?.source
        ? (currentItem.source.startsWith('blob:') || currentItem.source.startsWith('http://') || currentItem.source.startsWith('https://')
            ? currentItem.source
            : `${process.env.REACT_APP_API_ENDPOINT}/${currentItem.source}`)
        : '';

    const docUrl = documentoUrl
        ? (documentoUrl.startsWith('blob:') || documentoUrl.startsWith('http://') || documentoUrl.startsWith('https://')
            ? documentoUrl
            : `${process.env.REACT_APP_API_ENDPOINT}/${documentoUrl}`)
        : '';

    return (
        <FormContainer size="xl" show={show} onHide={onClose}>
            <FormContainer.Header onClose={onClose}>
                <h5>
                    {tipo === 'foto' ? `Foto ${index + 1} de ${items.length}` : 'Documento'}
                    {currentItem?.tipo && ` - ${currentItem.tipo}`}
                </h5>
            </FormContainer.Header>
            <FormContainer.Body noPadding={true}>
                {tipo === 'foto' && (
                    <div className="visualizador-container">
                        <div className="visualizador-image-wrapper">
                            {index > 0 && (
                                <button
                                    type="button"
                                    className="visualizador-nav visualizador-nav-prev"
                                    onClick={handlePrevious}
                                    title="Anterior (←)"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                            )}

                            <img
                                src={imageUrl}
                                alt={`Foto ${index + 1}`}
                                className="visualizador-image"
                            />

                            {index < items.length - 1 && (
                                <button
                                    type="button"
                                    className="visualizador-nav visualizador-nav-next"
                                    onClick={handleNext}
                                    title="Próxima (→)"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                            )}
                        </div>

                        {items.length > 1 && (
                            <div className="visualizador-thumbnails">
                                {items.map((item, idx) => {
                                    const thumbUrl = item.source
                                        ? (item.source.startsWith('blob:') || item.source.startsWith('http://') || item.source.startsWith('https://')
                                            ? item.source
                                            : `${process.env.REACT_APP_API_ENDPOINT}/${item.source}`)
                                        : '';

                                    return (
                                        <div
                                            key={idx}
                                            className={`visualizador-thumbnail ${idx === index ? 'active' : ''}`}
                                            onClick={() => setIndex(idx)}
                                        >
                                            <img src={thumbUrl} alt={`Thumb ${idx + 1}`} />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {tipo === 'documento' && (
                    <div className="visualizador-documento">
                        <iframe
                            src={docUrl}
                            title="Visualizador de Documento"
                            className="visualizador-iframe"
                        />
                    </div>
                )}
            </FormContainer.Body>
            <FormContainer.Footer>
                {tipo === 'foto' && items.length > 1 && (
                    <div className="visualizador-footer-info">
                        Use ← e → para navegar
                    </div>
                )}
                <Button variant="secondary" onClick={onClose}>Fechar</Button>
            </FormContainer.Footer>
        </FormContainer>
    );
}
